import { computed, onBeforeUnmount, ref } from 'vue'
import { useStore } from '@web/store'
import Logger from '@web/common/Logger'
import { AmoCrmWsClient } from '@web/api/careChat/AmoCrmWsClient'
import { generateUid } from '@web/api/careChat/uidGenerator'
import {
  careChatButtonHash,
  careChatButtonId,
  careChatEnabled,
  careChatGsoBaseUrl,
  careChatLeadFieldId
} from '@web/common/ExternalConfig/getters'
import {
  CARE_CHAT_CONNECTION_STATUS,
  CARE_CHAT_MESSAGE_TYPE,
  CARE_CHAT_SENDER_TYPE,
  CARE_CHAT_SEND_STATUS,
  CareChatMessage,
  CareChatMessageType
} from '@web/types/careChat'
import {
  CARE_CHAT_EVENT_TYPE,
  CareChatEvent
} from '@web/types/careChatEvent'

function detectMediaType (file: File): CareChatMessageType {
  if (file.type.startsWith('image/')) return CARE_CHAT_MESSAGE_TYPE.PICTURE
  if (file.type.startsWith('video/')) return CARE_CHAT_MESSAGE_TYPE.VIDEO
  return CARE_CHAT_MESSAGE_TYPE.FILE
}

// amoCRM/Kommo возвращает URL аплоада под разными ключами: для изображений
// под `photo`, для видео под `video`, для файлов под `file`/`attachment`,
// иногда на верхнем уровне. Перебираем все известные варианты.
// Реальный ответ amoCRM на jpg: { photo: { url, thumbnail, file_name, ... } }.
type AttachmentLike = Record<string, unknown> | null | undefined
const NESTED_ATTACHMENT_KEYS = ['photo', 'video', 'file', 'attachment', 'data'] as const
function pickString (source: AttachmentLike, key: string): string | undefined {
  const value = source?.[key]
  return typeof value === 'string' && value.length > 0 ? value : undefined
}
function pickUrlFrom (source: AttachmentLike): string | undefined {
  return pickString(source, 'url') ?? pickString(source, 'link') ?? pickString(source, 'file_url')
}
function pickThumbnailFrom (source: AttachmentLike): string | undefined {
  return pickString(source, 'thumbnail') ?? pickString(source, 'thumb') ?? pickString(source, 'preview')
}
function extractAttachmentUrl (attachment: AttachmentLike): string | undefined {
  const direct = pickUrlFrom(attachment)
  if (direct) return direct
  for (const key of NESTED_ATTACHMENT_KEYS) {
    const nested = pickUrlFrom(attachment?.[key] as AttachmentLike)
    if (nested) return nested
  }
  return undefined
}
function extractAttachmentThumbnail (attachment: AttachmentLike): string | undefined {
  const direct = pickThumbnailFrom(attachment)
  if (direct) return direct
  for (const key of NESTED_ATTACHMENT_KEYS) {
    const nested = pickThumbnailFrom(attachment?.[key] as AttachmentLike)
    if (nested) return nested
  }
  return undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCommit = (key: string, payload?: any) => void

let sharedClient: AmoCrmWsClient | null = null
let sharedClientDisposer: (() => void) | null = null

function getOrCreateClient (): AmoCrmWsClient | null {
  if (sharedClient) return sharedClient
  if (!careChatEnabled()) return null
  const gsoBaseUrl = careChatGsoBaseUrl()
  const buttonId = careChatButtonId()
  const buttonHash = careChatButtonHash()
  if (!gsoBaseUrl || !buttonId || !buttonHash) {
    Logger.warn('[useCareChat] care chat config is incomplete', { gsoBaseUrl, buttonId, hasHash: Boolean(buttonHash) })
    return null
  }
  const leadFieldId = careChatLeadFieldId()
  sharedClient = new AmoCrmWsClient({ gsoBaseUrl, buttonId, buttonHash, leadFieldId })
  return sharedClient
}

export function disposeCareChatClient (): void {
  if (sharedClientDisposer) {
    sharedClientDisposer()
    sharedClientDisposer = null
  }
  if (sharedClient) {
    sharedClient.dispose()
    sharedClient = null
  }
}

export default function useCareChat () {
  const store = useStore()
  const commit = store.commit as unknown as AnyCommit

  const isEnabled = computed<boolean>(() => careChatEnabled())
  const messages = computed<CareChatMessage[]>(() => store.getters['careChat/messages'])
  const unreadCount = computed<number>(() => store.getters['careChat/unreadCount'])
  const connectionStatus = computed(() => store.getters['careChat/connectionStatus'])
  const isManagerTyping = computed<boolean>(() => store.getters['careChat/isManagerTyping'])

  const isConnecting = ref(false)

  function bindEvents (client: AmoCrmWsClient): void {
    if (sharedClientDisposer) return
    sharedClientDisposer = client.addEventListener((event: CareChatEvent) => {
      switch (event.type) {
        case CARE_CHAT_EVENT_TYPE.CONNECTED:
          commit('careChat/setConnectionStatus', CARE_CHAT_CONNECTION_STATUS.CONNECTED)
          commit('careChat/setConversationContext', {
            chatId: event.chatId ?? null,
            conversationId: event.conversationIdList[0] ?? null
          })
          commit('careChat/setLastError', null)
          // Историю запрашивает сам WS-клиент в handleInitResponse →
          // requestMissedMessages (полная на первом коннекте, missed на реконнекте).
          break
        case CARE_CHAT_EVENT_TYPE.DISCONNECTED:
          commit('careChat/setConnectionStatus', CARE_CHAT_CONNECTION_STATUS.DISCONNECTED)
          break
        case CARE_CHAT_EVENT_TYPE.RECONNECTING:
          commit('careChat/setConnectionStatus', CARE_CHAT_CONNECTION_STATUS.RECONNECTING)
          break
        case CARE_CHAT_EVENT_TYPE.CONNECTION_GAVE_UP:
          commit('careChat/setConnectionStatus', CARE_CHAT_CONNECTION_STATUS.ERROR)
          commit('careChat/setLastError', `connection_gave_up:${event.totalAttemptCount}`)
          break
        case CARE_CHAT_EVENT_TYPE.HISTORY:
          commit('careChat/receiveMessages', event.messages)
          break
        case CARE_CHAT_EVENT_TYPE.NEW_MESSAGE:
          commit('careChat/receiveMessage', event.message)
          if (typeof event.serverBadgeCount === 'number') {
            commit('careChat/setUnreadCount', event.serverBadgeCount)
          } else if (event.message.senderType === CARE_CHAT_SENDER_TYPE.MANAGER) {
            commit('careChat/setUnreadCount', store.getters['careChat/unreadCount'] + 1)
          }
          // Заложенный механизм снекбара: для входящего сообщения менеджера
          // выставляем уведомление. UI-снекбар (потребитель) сам решит,
          // показывать ли его в зависимости от текущего роута, и вызовет
          // careChat/clearIncomingNotification.
          if (event.message.senderType === CARE_CHAT_SENDER_TYPE.MANAGER) {
            commit('careChat/setIncomingNotification', event.message)
          }
          break
        case CARE_CHAT_EVENT_TYPE.MESSAGE_SENT:
          commit('careChat/receiveMessage', {
            ...event.message,
            localUid: event.localUid,
            sendStatus: CARE_CHAT_SEND_STATUS.SENT
          })
          break
        case CARE_CHAT_EVENT_TYPE.BADGE_UPDATED:
          commit('careChat/setUnreadCount', event.badgeCount)
          break
        case CARE_CHAT_EVENT_TYPE.MANAGER_TYPING:
          commit('careChat/setManagerTyping', event.expiredAtTimestamp ?? null)
          break
        case CARE_CHAT_EVENT_TYPE.CONVERSATION_CLOSED:
          commit('careChat/setManagerTyping', null)
          break
        case CARE_CHAT_EVENT_TYPE.REQUEST_FAILED:
          commit('careChat/setLastError', event.errorMessage)
          break
        case CARE_CHAT_EVENT_TYPE.ERROR:
          commit('careChat/setLastError', event.errorMessage)
          break
      }
    })
  }

  async function connect (): Promise<void> {
    if (!isEnabled.value) return
    const user = store.getters['user/user']
    if (!user?.id) {
      Logger.warn('[useCareChat] connect skipped: no authorized user')
      return
    }
    const client = getOrCreateClient()
    if (!client) return
    if (client.isConnected) return

    bindEvents(client)
    commit('careChat/setConnectionStatus', CARE_CHAT_CONNECTION_STATUS.CONNECTING)
    isConnecting.value = true
    try {
      await client.connect({
        userId: String(user.id),
        userName: user.firstName || `user-${user.id}`,
        phone: user.phone,
        email: user.email
      })
    } catch (error) {
      Logger.error('[useCareChat] connect failed', error)
      commit('careChat/setConnectionStatus', CARE_CHAT_CONNECTION_STATUS.ERROR)
    } finally {
      isConnecting.value = false
    }
  }

  function markOptimisticFailed (localUid: string, errorMessage: string): void {
    commit('careChat/receiveMessage', {
      localUid,
      sendStatus: CARE_CHAT_SEND_STATUS.FAILED
    })
    commit('careChat/setLastError', errorMessage)
  }

  async function sendMessage (text: string): Promise<string | null> {
    const client = sharedClient
    if (!client) return null
    const trimmed = text.trim()
    if (!trimmed) return null

    // Оптимистично кладём сообщение со SENDING — UI сразу видит свою реплику,
    // даже если сейчас придётся ждать форсированный реконнект.
    const localUid = generateUid()
    commit('careChat/receiveMessage', {
      localUid,
      text: trimmed,
      type: CARE_CHAT_MESSAGE_TYPE.TEXT,
      senderType: CARE_CHAT_SENDER_TYPE.USER,
      timestamp: Math.floor(Date.now() / 1000),
      sendStatus: CARE_CHAT_SEND_STATUS.SENDING
    })

    try {
      await client.ensureConnectedOrReconnect()
    } catch (error) {
      Logger.warn('[useCareChat] sendMessage: reconnect failed', error)
      markOptimisticFailed(localUid, 'reconnect_failed')
      return null
    }

    const sentUid = client.sendMessage(trimmed, localUid)
    if (!sentUid) {
      markOptimisticFailed(localUid, 'send_failed')
      return null
    }
    return sentUid
  }

  async function sendFile (file: File): Promise<string | null> {
    const client = sharedClient
    if (!client) return null

    // Оптимистично кладём медиа-сообщение с локальным превью (blob URL),
    // чтобы пользователь видел индикатор загрузки на крупных файлах:
    // аплоад до WS-отправки может занимать секунды.
    const localUid = generateUid()
    const localPreviewUrl = URL.createObjectURL(file)
    const mediaType = detectMediaType(file)
    commit('careChat/receiveMessage', {
      localUid,
      type: mediaType,
      senderType: CARE_CHAT_SENDER_TYPE.USER,
      timestamp: Math.floor(Date.now() / 1000),
      mediaUrl: localPreviewUrl,
      thumbnailUrl: localPreviewUrl,
      fileName: file.name,
      fileSize: file.size,
      sendStatus: CARE_CHAT_SEND_STATUS.SENDING
    })

    try {
      await client.ensureConnectedOrReconnect()
    } catch (error) {
      Logger.warn('[useCareChat] sendFile: reconnect failed', error)
      URL.revokeObjectURL(localPreviewUrl)
      markOptimisticFailed(localUid, 'reconnect_failed')
      return null
    }

    const attachment = await client.uploadAttachment(file)
    if (!attachment) {
      URL.revokeObjectURL(localPreviewUrl)
      markOptimisticFailed(localUid, 'upload_failed')
      return null
    }

    // Сокет мог умереть пока заливался файл — пере-проверяем перед WS-отправкой.
    try {
      await client.ensureConnectedOrReconnect()
    } catch (error) {
      Logger.warn('[useCareChat] sendFile: reconnect after upload failed', error)
      URL.revokeObjectURL(localPreviewUrl)
      markOptimisticFailed(localUid, 'reconnect_failed')
      return null
    }

    const sentUid = client.sendFileWithAttachment(attachment, localUid)
    if (!sentUid) {
      URL.revokeObjectURL(localPreviewUrl)
      markOptimisticFailed(localUid, 'send_failed')
      return null
    }

    // Подменяем blob-превью на серверный URL ИЗ ЗАГРУЖЕННОГО attachment'а,
    // не дожидаясь MESSAGE_SENT: серверный echo не всегда возвращает media
    // обратно в payload, и без подмены пользователь видит превью, но не
    // может ни открыть в модалке, ни скачать (blob ревокнут или невалиден).
    const serverMediaUrl = extractAttachmentUrl(attachment)
    const serverThumbnailUrl = extractAttachmentThumbnail(attachment)
    if (serverMediaUrl) {
      commit('careChat/receiveMessage', {
        localUid,
        mediaUrl: serverMediaUrl,
        thumbnailUrl: serverThumbnailUrl ?? serverMediaUrl
      })
      URL.revokeObjectURL(localPreviewUrl)
    }
    return sentUid
  }

  function markAllRead (): void {
    const client = sharedClient
    if (!client) return
    const lastTimestamp = store.getters['careChat/lastIncomingTimestamp']
    const fallbackTimestamp = Math.floor(Date.now() / 1000)
    const timestamp = lastTimestamp > 0 ? lastTimestamp : fallbackTimestamp
    client.markAsRead(timestamp)
    commit('careChat/resetUnreadCount')
  }

  function requestHistory (params?: { lteTimestamp?: number; gteTimestamp?: number }): void {
    sharedClient?.requestHistory(params)
  }

  function disconnect (): void {
    if (!sharedClient) return
    sharedClient.disconnect()
  }

  onBeforeUnmount(() => {
    // По умолчанию НЕ закрываем клиент при размонтировании конкретного компонента —
    // он переиспользуется на разных страницах. Закрытие — только при логауте или
    // явном вызове disposeCareChatClient.
  })

  return {
    isEnabled,
    isConnecting,
    messages,
    unreadCount,
    connectionStatus,
    isManagerTyping,
    connect,
    disconnect,
    sendMessage,
    sendFile,
    markAllRead,
    requestHistory
  }
}
